import classNames from 'classnames';
import { useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { Icon } from '../Icon';
import { Typography } from '../Typography';

import styles from './Calendar.module.css';
import {
  addDays,
  addMonths,
  buildMonthGrid,
  DAYS_IN_WEEK,
  DEFAULT_MONTH_LABELS,
  DEFAULT_WEEKDAY_FULL,
  DEFAULT_WEEKDAY_LABELS,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithin,
  monthTitle,
  startOfDay,
  startOfMonth,
  weekdayOrder,
  WEEKS_SHOWN,
} from './dates';
import type { DateRange } from './dates';

export type { DateRange } from './dates';

export type CalendarMode = 'single' | 'range';
export type CalendarSelection = Date | DateRange | null;

export interface CalendarProps {
  mode?: CalendarMode;
  /** Selected value — a `Date` in single mode, a `DateRange` in range mode. */
  selected?: CalendarSelection;
  defaultSelected?: CalendarSelection;
  onSelect?: (selected: CalendarSelection) => void;
  /** Controlled first visible month. */
  month?: Date;
  defaultMonth?: Date;
  onMonthChange?: (month: Date) => void;
  /** Show one or two side-by-side months (clamped to that range). */
  numberOfMonths?: number;
  /** 0 = Sunday, 1 = Monday (default, matching the Figma). */
  weekStartsOn?: 0 | 1;
  minDate?: Date;
  maxDate?: Date;
  /** Disable arbitrary days (e.g. weekends, blackout dates). */
  isDateDisabled?: (date: Date) => boolean;
  /** Render the small event dot under a day. */
  hasDot?: (date: Date) => boolean;
  /** Month names (Jan…Dec) and Sunday-first weekday labels — the package has no i18n dep. */
  monthLabels?: string[];
  weekdayLabels?: string[];
  weekdayLabelsFull?: string[];
  /** Slot rendered between the month header and the day grid (e.g. a date input row). */
  belowHeaderSlot?: ReactNode;
  className?: string;
}

const MONTH_STEP = 1;
const WEEK_STEP = DAYS_IN_WEEK;
const MAX_MONTHS = 2;
const TODAY_ONCE = startOfDay(new Date());

function isRange(mode: CalendarMode, value: CalendarSelection): value is DateRange {
  return mode === 'range' && value != null && !(value instanceof Date);
}

function firstSelectedDate(value: CalendarSelection): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return value;
  return value.from;
}

/**
 * Month calendar grid following the WAI-ARIA date-picker pattern (`role="grid"`, roving
 * tabindex over day buttons, arrow/Home/End/PageUp-Down navigation). Selects a single date or
 * a range (with a live hover preview), shows one or two months, marks today, disables days and
 * renders an optional per-day event dot. Selection and the visible month can be controlled or
 * uncontrolled; all labels arrive as props (no i18n dependency).
 *
 * @interim Seeded from the Figma "Date picker"; replaceable by the UI Kit without API breakage.
 */
export function Calendar({
  mode = 'single',
  selected,
  defaultSelected = null,
  onSelect,
  month,
  defaultMonth,
  onMonthChange,
  numberOfMonths = 1,
  weekStartsOn = 1,
  minDate,
  maxDate,
  isDateDisabled,
  hasDot,
  monthLabels,
  weekdayLabels,
  weekdayLabelsFull,
  belowHeaderSlot,
  className,
}: CalendarProps) {
  const messages = useUiMessages();
  const resolvedMonthLabels = monthLabels ?? messages.calendar?.months ?? DEFAULT_MONTH_LABELS;
  const resolvedWeekdayLabels =
    weekdayLabels ?? messages.calendar?.weekdaysShort ?? DEFAULT_WEEKDAY_LABELS;
  const resolvedWeekdayLabelsFull =
    weekdayLabelsFull ?? messages.calendar?.weekdaysFull ?? DEFAULT_WEEKDAY_FULL;
  const previousMonthLabel = messages.calendar?.previousMonth ?? 'Previous month';
  const nextMonthLabel = messages.calendar?.nextMonth ?? 'Next month';
  const isSelControlled = selected !== undefined;
  const [internalSel, setInternalSel] = useState<CalendarSelection>(defaultSelected);
  const value = isSelControlled ? selected : internalSel;
  const monthCount = numberOfMonths >= MAX_MONTHS ? MAX_MONTHS : 1;

  const initialMonth = startOfMonth(
    month ?? defaultMonth ?? firstSelectedDate(value ?? null) ?? TODAY_ONCE,
  );
  const isMonthControlled = month !== undefined;
  const [internalMonth, setInternalMonth] = useState(initialMonth);
  const viewMonth = isMonthControlled ? startOfMonth(month) : internalMonth;

  const [focused, setFocused] = useState<Date>(
    () => firstSelectedDate(value ?? null) ?? TODAY_ONCE,
  );
  const [hovered, setHovered] = useState<Date | null>(null);
  const dayRefs = useRef(new Map<string, HTMLButtonElement>());

  const setMonth = (next: Date) => {
    const normalized = startOfMonth(next);
    if (!isMonthControlled) setInternalMonth(normalized);
    onMonthChange?.(normalized);
  };

  const isDisabled = (date: Date) => {
    if (minDate != null && isBefore(date, minDate)) return true;
    if (maxDate != null && isAfter(date, maxDate)) return true;
    return isDateDisabled?.(date) ?? false;
  };

  const commitSelection = (day: Date) => {
    if (isDisabled(day)) return;
    const picked = startOfDay(day);
    if (mode === 'single') {
      const next = picked;
      if (!isSelControlled) setInternalSel(next);
      onSelect?.(next);
      return;
    }
    const current = isRange(mode, value) ? value : null;
    let next: DateRange;
    if (current?.from == null || current.to != null) {
      next = { from: picked, to: null };
    } else if (isBefore(picked, current.from)) {
      next = { from: picked, to: current.from };
    } else {
      next = { from: current.from, to: picked };
    }
    if (!isSelControlled) setInternalSel(next);
    onSelect?.(next);
  };

  const keyFor = (d: Date) =>
    `${String(d.getFullYear())}-${String(d.getMonth())}-${String(d.getDate())}`;

  const focusDate = (next: Date) => {
    setFocused(next);
    // Keep the target month visible.
    const lastVisible = addMonths(viewMonth, monthCount - 1);
    if (isBefore(next, startOfMonth(viewMonth))) setMonth(startOfMonth(next));
    else if (isAfter(next, addDays(addMonths(lastVisible, 1), -1))) {
      setMonth(addMonths(startOfMonth(next), -(monthCount - 1)));
    }
    requestAnimationFrame(() => dayRefs.current.get(keyFor(next))?.focus());
  };

  const onGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let next: Date;
    switch (event.key) {
      case 'ArrowLeft':
        next = addDays(focused, -MONTH_STEP);
        break;
      case 'ArrowRight':
        next = addDays(focused, MONTH_STEP);
        break;
      case 'ArrowUp':
        next = addDays(focused, -WEEK_STEP);
        break;
      case 'ArrowDown':
        next = addDays(focused, WEEK_STEP);
        break;
      case 'Home':
        next = addDays(focused, -((focused.getDay() - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK));
        break;
      case 'End':
        next = addDays(
          focused,
          DAYS_IN_WEEK - 1 - ((focused.getDay() - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK),
        );
        break;
      case 'PageUp':
        next = addMonths(focused, -MONTH_STEP);
        break;
      case 'PageDown':
        next = addMonths(focused, MONTH_STEP);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commitSelection(focused);
        return;
      default:
        return;
    }
    event.preventDefault();
    focusDate(next);
  };

  const range = isRange(mode, value) ? value : null;
  const previewTo = range?.from != null && range.to == null ? hovered : null;

  const dayState = (day: Date) => {
    const single = mode === 'single' && value instanceof Date ? value : null;
    const isSelected =
      isSameDay(day, single) ||
      isSameDay(day, range?.from ?? null) ||
      isSameDay(day, range?.to ?? null);
    let inRange = false;
    let isEndpoint = isSelected;
    let isStart = false;
    let isEnd = false;
    if (range?.from != null) {
      const end = range.to ?? previewTo;
      if (end != null) {
        // Round by chronological order, not selection order, so a backwards range
        // (later day picked first) still rounds its left/right edges correctly.
        const lo = isBefore(end, range.from) ? end : range.from;
        const hi = isBefore(end, range.from) ? range.from : end;
        inRange = isWithin(day, lo, hi);
        isEndpoint = isSameDay(day, lo) || isSameDay(day, hi);
        isStart = isSameDay(day, lo);
        isEnd = isSameDay(day, hi);
      }
    }
    return { isSelected, inRange, isEndpoint, isStart, isEnd };
  };

  const weekdays = weekdayOrder(weekStartsOn);
  const months = Array.from({ length: monthCount }, (_, i) => addMonths(viewMonth, i));

  return (
    <div className={classNames(styles.root, monthCount > 1 && styles.multi, className)}>
      {months.map((m, mi) => {
        const cells = buildMonthGrid(m, weekStartsOn);
        const title = monthTitle(m, resolvedMonthLabels);
        return (
          <div key={mi} className={styles.month}>
            <div className={styles.header}>
              {mi === 0 ? (
                <button
                  type="button"
                  className={styles.nav}
                  aria-label={previousMonthLabel}
                  onClick={() => {
                    setMonth(addMonths(viewMonth, -MONTH_STEP));
                  }}
                >
                  <Icon name="chevron-left" size={20} />
                </button>
              ) : (
                <span className={styles.navSpacer} />
              )}
              <Typography
                as="span"
                variant="text-sm"
                weight="semibold"
                className={classNames(styles.title)}
              >
                {title}
              </Typography>
              {mi === monthCount - 1 ? (
                <button
                  type="button"
                  className={styles.nav}
                  aria-label={nextMonthLabel}
                  onClick={() => {
                    setMonth(addMonths(viewMonth, MONTH_STEP));
                  }}
                >
                  <Icon name="chevron-right" size={20} />
                </button>
              ) : (
                <span className={styles.navSpacer} />
              )}
            </div>

            {mi === 0 && belowHeaderSlot != null && (
              <div className={styles.belowHeader}>{belowHeaderSlot}</div>
            )}

            {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus -- focus lives on the day buttons (roving tabindex); the grid only hosts the arrow-key handler per the APG date-picker pattern */}
            <div role="grid" aria-label={title} className={styles.grid} onKeyDown={onGridKeyDown}>
              <div role="row" className={styles.weekdays}>
                {weekdays.map((wd) => (
                  <span
                    key={wd}
                    role="columnheader"
                    aria-label={resolvedWeekdayLabelsFull[wd]}
                    className={styles.weekday}
                  >
                    {resolvedWeekdayLabels[wd]}
                  </span>
                ))}
              </div>
              {Array.from({ length: WEEKS_SHOWN }, (_, w) => (
                <div key={w} role="row" className={styles.week}>
                  {cells.slice(w * DAYS_IN_WEEK, w * DAYS_IN_WEEK + DAYS_IN_WEEK).map((day) => {
                    const outside = !isSameMonth(day, m);
                    const disabled = isDisabled(day);
                    const isToday = isSameDay(day, TODAY_ONCE);
                    const { isSelected, inRange, isEndpoint, isStart, isEnd } = dayState(day);
                    // Only render each day once (in its own month) when showing two months.
                    if (outside && monthCount > 1) {
                      // Keep a blank gridcell in the accessibility tree so every row retains
                      // the required seven-cell grid structure without announcing the date twice.
                      return <span key={keyFor(day)} role="gridcell" className={styles.cell} />;
                    }
                    const tabbable = isSameDay(day, focused);
                    return (
                      <div
                        key={keyFor(day)}
                        role="gridcell"
                        aria-selected={isEndpoint || isSelected}
                        className={classNames(styles.cell, {
                          [String(styles.inRange)]: inRange,
                          [String(styles.rangeStart)]: inRange && isStart,
                          [String(styles.rangeEnd)]: inRange && isEnd,
                        })}
                      >
                        <button
                          type="button"
                          ref={(el) => {
                            if (el != null) dayRefs.current.set(keyFor(day), el);
                            else dayRefs.current.delete(keyFor(day));
                          }}
                          className={classNames(styles.day, {
                            [String(styles.selected)]: isEndpoint || isSelected,
                            [String(styles.outside)]: outside,
                            [String(styles.today)]: isToday && !isSelected && !isEndpoint,
                          })}
                          tabIndex={tabbable ? 0 : -1}
                          disabled={disabled}
                          aria-current={isToday ? 'date' : undefined}
                          aria-label={`${resolvedWeekdayLabelsFull[day.getDay()] ?? ''}, ${resolvedMonthLabels[day.getMonth()] ?? ''} ${String(day.getDate())}, ${String(day.getFullYear())}`}
                          onClick={() => {
                            setFocused(startOfDay(day));
                            commitSelection(day);
                          }}
                          onMouseEnter={() => {
                            setHovered(startOfDay(day));
                          }}
                          onFocus={() => {
                            setHovered(startOfDay(day));
                          }}
                        >
                          <span className={styles.dayNumber}>{day.getDate()}</span>
                          {hasDot?.(day) === true && (
                            <span className={styles.dot} aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
